import { gql, useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import IconCheck from '@apollo/icons/default/IconCheck.svg';
import IconCollapseList from '@apollo/icons/default/IconCollapseList.svg';
import IconCommit from '@apollo/icons/default/IconCommit.svg';
import IconExpandList from '@apollo/icons/default/IconExpandList.svg';
import IconHistory from '@apollo/icons/default/IconHistory.svg';
import IconServices from '@apollo/icons/default/IconServices.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import IconWarningSolid from '@apollo/icons/default/IconWarningSolid.svg';
import {
  Alert,
  Avatar,
  Button,
  Card,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Link,
  Switch,
} from '@apollo/orbit';
import { Moon } from '@apollo/space-kit/illustrations/Moon';
import classnames from 'classnames';
import _ from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useResizeDetector } from 'react-resize-detector';
import {
  Link as ReactRouterLink,
  Redirect,
  useHistory,
  useLocation,
} from 'react-router-dom';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorPage } from 'src/components/errorPage/ErrorPage';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import {
  LocalTimestamp,
  RelativeTimestamp,
} from 'src/components/timestamp/Timestamp';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { Config } from 'src/lib/config/config';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { setOverlayVisibleLocation } from 'src/lib/routing';
import { safeCompileMarkdown } from 'src/lib/safeCompileMarkdown';

import { StyledMenuNavLink } from '../../documentationPage/schemaPage/styledMenuNavLink/StyledMenuNavLink';
import {
  API_SCHEMA_ID,
  SUPERGRAPH_SCHEMA_ID,
} from '../../documentationPage/sdlPage/sdlDisplay/SDLDisplay';
import {
  SupergraphAndApiSchemaChangesFragment,
  UseRemovedCoordinatesLookupGraphFragment,
} from '../../documentationPage/useRemovedCoordinatesLookup/useRemovedCoordinatesLookup';
import { GraphRef } from '../../hooks/useGraphRef';
import {
  proposalChangesRouteConfig,
  proposalChecksListRouteConfig,
  proposalEditorRouteConfig,
} from '../../routes';
import { FlatDiffItemFragment } from '../diffHelpers/diffHelpers';
import { DiffSummary, DiffSummaryDiffFragment } from '../DiffSummary';
import { ImplementedChangesFragment } from '../implementationGutterIndicator/ImplementationGutterIndicator';
import { DeleteCommentModal } from '../proposalCommentDrawer/deleteCommentModal/DeleteCommentModal';
import { ProposalCommentDrawer } from '../proposalCommentDrawer/ProposalCommentDrawer';
import { ProposalCommentsParentCommentFragment } from '../proposalCommentDrawer/shared';

import { ChangesDiffs } from './ChangesDiffs';
import { RevisionHistoryDrawer } from './revisionHistoryDrawer/RevisionHistoryDrawer';

const ProposalRevisionInfoFragment = gql`
  fragment ProposalRevisionInfoFragment on ProposalRevision {
    id
    createdAt
    createdBy {
      id
      ... on User {
        fullName
        avatarUrl
      }
    }
    summary
  }
`;

const RevisionHistoryLaunchFragment = gql`
  fragment RevisionHistoryLaunchFragment on Launch {
    id
    buildInput {
      ... on CompositionBuildInput {
        subgraphs {
          name
          hash
        }
      }
    }
    build {
      result {
        ... on BuildSuccess {
          coreSchema {
            coreHash
          }
        }
      }
    }
  }
`;

function EmptyState() {
  const location = useLocation();
  return (
    <div className="w-120 text-center">
      <Moon className="mb-3" />
      <Heading size="2xl" className="mb-3 text-center">
        View schema changes quickly and easily
      </Heading>
      <p className="max-w-[65ch] text-center text-base text-secondary">
        Head over to the editor to propose updates to one or more subgraph
        schemas. Once you've saved a revision, a summary and detailed view of
        your changes appears on this screen.
      </p>
      <Button
        className="mt-4"
        as={ReactRouterLink}
        to={proposalEditorRouteConfig.locationFrom({
          location,
          fromRouteConfig: proposalChangesRouteConfig,
        })}
      >
        Start editing
      </Button>
    </div>
  );
}

const SummaryHeader = () => {
  return (
    <>
      <h1 className="text-2xl font-semibold">Summary</h1>
      <div className="pb-6 pt-1 text-secondary">
        A quick glimpse at all proposed schema changes for this subgraph.
      </div>
    </>
  );
};

const SummarySection = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="px-8 py-4">
      <SummaryHeader />
      {children}
    </div>
  );
};

function SchemaLink({
  schemaName,
  selectedSchemaName,
  className,
  buildErrors,
}: {
  schemaName: string;
  selectedSchemaName: string;
  className?: string;
  buildErrors: GraphQLTypes.BuildError[] | undefined;
}) {
  const location = useLocation();
  return (
    <StyledMenuNavLink
      className={className}
      to={proposalChangesRouteConfig.locationFrom({
        location,
        patch: {
          selectedSchema: schemaName,
          linkedGroupName: RouteConfig.DELETE,
          linkedCoordinateName: RouteConfig.DELETE,
        },
      })}
      isActive={() => selectedSchemaName === schemaName}
    >
      {schemaName === API_SCHEMA_ID || schemaName === SUPERGRAPH_SCHEMA_ID ? (
        <>
          <IconServices
            className={classnames(
              'ml-0.5 mr-2 mt-1',
              selectedSchemaName === schemaName
                ? 'text-blilet'
                : 'text-icon-primary',
            )}
          />
          <span
            className={classnames(
              'inline-block font-mono text-sm',
              (schemaName === API_SCHEMA_ID ||
                schemaName === SUPERGRAPH_SCHEMA_ID) &&
                isMinLengthArray(1, buildErrors) &&
                'mr-1',
            )}
          >
            {schemaName === API_SCHEMA_ID ? 'API Schema' : 'Supergraph Schema'}
          </span>
          {isMinLengthArray(1, buildErrors) && (
            <IconWarningSolid className="ml-auto mt-1 text-icon-warning" />
          )}
        </>
      ) : (
        <>
          <IconSingleService
            className={classnames(
              'ml-0.5 mr-2 mt-1',
              selectedSchemaName === schemaName
                ? 'text-blilet'
                : 'text-icon-primary',
            )}
          />
          <span className="font-mono text-sm">{schemaName}</span>
        </>
      )}
    </StyledMenuNavLink>
  );
}

export const ProposalChanges = ({
  graphRef,
  historyLength = 0,
  isStale,
}: {
  graphRef: GraphRef;
  historyLength: number | undefined;
  isStale: boolean;
}) => {
  const containerSize = useResizeDetector({ handleHeight: false });
  const {
    selectedSchema,
    selectedRevision: linkedSelectedRevision,
    linkedGroupName,
    linkedCoordinateName,
    linkedCommentId,
    includeImplementedChanges: includeImplementedChangesParam,
  } = useRouteParams(proposalChangesRouteConfig);
  const location = useLocation();
  const history = useHistory();
  const includeImplementedChanges = includeImplementedChangesParam === 'true'; // defaults to false
  const {
    loading: permissionsLoading,
    data: { graphVariantPermissions },
  } = usePermissions(undefined, graphRef?.graphId, graphRef?.graphVariant);
  const canQueryLaunches = graphVariantPermissions?.canQueryLaunches ?? false;

  const shouldShowRevisionHistoryUI = useLDFlag(
    'nebula-proposals-revision-history',
  );
  const shouldShowRevisionHistoryDrawer =
    shouldShowRevisionHistoryUI && !!linkedSelectedRevision;

  const [
    shouldPollForApiAndSupergraphSchema,
    setShouldPollForApiAndSupergraphSchema,
  ] = useState(true);

  const onCloseDrawer = useCallback(
    () =>
      history.push(
        proposalChangesRouteConfig.locationFrom({
          location,
          patch: {
            linkedCoordinateName: RouteConfig.DELETE,
            linkedGroupName: RouteConfig.DELETE,
            selectedRevision: RouteConfig.DELETE,
          },
          fromRouteConfig: proposalChangesRouteConfig,
        }),
      ),
    [history, location],
  );

  const {
    data,
    loading,
    error: mainError,
  } = useQuery<
    GraphQLTypes.ProposalChangesQuery,
    GraphQLTypes.ProposalChangesQueryVariables
  >(
    gql`
      # 🚨IMPORTANT🚨: Do not rename this query. We use it in our Proposals SLO by name.
      query ProposalChangesQuery(
        $graphId: ID!
        $graphVariant: String!
        $historyOffset: Int!
        $shouldSkipFetchingMergeBase: Boolean!
        $shouldSkipFetchingLaunch: Boolean!
      ) {
        service(id: $graphId) {
          id
          roles {
            canQueryImplementingServices
          }
          ...UseRemovedCoordinatesLookupGraphFragment
          variant(name: $graphVariant) {
            id
            proposal {
              status
              sourceVariant {
                id
                name
              }
              ...ImplementedChangesFragment @defer
              id
              updatedAt
              parentComments(filter: { type: [CHANGE] }) {
                ...ProposalCommentsParentCommentFragment
              }
              latestRevision {
                id
                summary
                mergeBaseSchemaPublish @skip(if: $shouldSkipFetchingMergeBase) {
                  id
                  schema {
                    createdAt
                    hash
                    document
                  }
                }
                createdBy {
                  id
                  ... on User {
                    fullName
                    avatarUrl
                  }
                }
                mergeBaseLaunch @skip(if: $shouldSkipFetchingMergeBase) {
                  id
                  ...RevisionHistoryLaunchFragment
                }
                launch {
                  id
                }
              }
            }
            latestApprovedLaunch {
              id
            }
            latestLaunch {
              id
              build {
                result {
                  ... on BuildFailure {
                    errorMessages {
                      code
                      locations {
                        column
                        line
                      }
                      message
                      failedStep
                    }
                  }
                }
              }
            }
          }
        }
      }
      ${UseRemovedCoordinatesLookupGraphFragment}
      ${ProposalCommentsParentCommentFragment}
      ${FlatDiffItemFragment}
      ${ImplementedChangesFragment}
      ${RevisionHistoryLaunchFragment}
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      variables: {
        ...graphRef,
        // TODO: historyLength seems to be off from the actual history length sometimes, this fetchs the last 10 because it seems to be sometimes shorter so this should hopefully catch it unless it's more than 10
        historyOffset: Math.max(0, historyLength - 10),
        shouldSkipFetchingMergeBase: !!linkedSelectedRevision,
        shouldSkipFetchingLaunch: !canQueryLaunches,
      },
    },
  );
  const graph = data?.service;
  const variant = graph?.variant;
  const proposal = variant?.proposal;
  const buildInput =
    variant?.latestLaunch?.buildInput.__typename === 'CompositionBuildInput'
      ? variant.latestLaunch.buildInput
      : undefined;
  const latestApprovedLaunchIdAsync = variant?.latestApprovedLaunch?.id;
  const latestLaunchBuildResult = variant?.latestLaunch?.build?.result;
  const launchIdSync = variant?.proposal?.latestRevision.launch?.id;
  const isLaunchFullyPropagated =
    !!latestApprovedLaunchIdAsync &&
    !!launchIdSync &&
    latestApprovedLaunchIdAsync === launchIdSync;

  const { data: selectedRevisionData, loading: selectedRevisionLoading } =
    useQuery<
      GraphQLTypes.ProposalChangesSelectedRevisionQuery,
      GraphQLTypes.ProposalChangesSelectedRevisionQueryVariables
    >(
      gql`
        query ProposalChangesSelectedRevisionQuery(
          $graphId: ID!
          $graphVariant: String!
          $revisionId: ID!
        ) {
          service(id: $graphId) {
            id
            variant(name: $graphVariant) {
              id
              proposal {
                id
                revision(id: $revisionId) {
                  ... on ProposalRevision {
                    id
                    ...ProposalRevisionInfoFragment
                    schemaPublish {
                      id
                      schema {
                        createdAt
                        hash
                        document
                      }
                    }
                    launch {
                      id
                      ...RevisionHistoryLaunchFragment
                    }
                    previousRevision {
                      id
                      schemaPublish {
                        id
                        schema {
                          createdAt
                          hash
                          document
                        }
                      }
                      launch {
                        id
                        ...RevisionHistoryLaunchFragment
                      }
                    }
                  }
                }
              }
            }
          }
        }
        ${ProposalRevisionInfoFragment}
        ${RevisionHistoryLaunchFragment}
      `,
      {
        context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
        variables: {
          ...graphRef,
          revisionId: linkedSelectedRevision as string, // this is safe because we only run this when it exists
        },
        skip: !linkedSelectedRevision,
      },
    );

  // Because certain revision info is available in the cache (because of the drawer)
  // we can use that to show the revision info while the launch info is loading in the background
  // this prevents the UI from flickering when switching between revisions in the drawer
  const client = useApolloClient();
  const cachedSelectedRevisionInfo =
    client.readFragment<GraphQLTypes.ProposalRevisionInfoFragment>({
      id: client.cache.identify({
        __typename: 'ProposalRevision',
        id: linkedSelectedRevision,
      }),
      fragment: ProposalRevisionInfoFragment,
    });

  const selectedRevision =
    selectedRevisionData?.service?.variant?.proposal?.revision?.__typename ===
    'ProposalRevision'
      ? selectedRevisionData.service.variant.proposal.revision
      : null;

  const [
    fetchSupergraphAndApiSchema,
    { loading: supergraphAndApiDataLoading },
  ] = useLazyQuery(
    gql`
      query ProposalChangesSupergraphAndApiSchemaPollQuery(
        $graphId: ID!
        $graphVariant: String!
        $shouldSkipFetchingLaunch: Boolean!
      ) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            ...SupergraphAndApiSchemaChangesFragment
          }
        }
      }
      ${SupergraphAndApiSchemaChangesFragment}
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      variables: {
        ...graphRef,
        shouldSkipFetchingLaunch: !canQueryLaunches,
      },
      pollInterval: shouldPollForApiAndSupergraphSchema ? 5000 : 0,
    },
  );

  useEffect(() => {
    if (!isLaunchFullyPropagated) {
      setShouldPollForApiAndSupergraphSchema(true);
    }
  }, [isLaunchFullyPropagated, shouldPollForApiAndSupergraphSchema]);

  // After a revision is published there may be a time when the user visits this
  // page and will see an outdated API/Supergraph schema since it is still composing.
  // We are polling here to auto-update the schema in the background so they don't need to
  // reload the page to see the changes
  useEffect(() => {
    if (
      !supergraphAndApiDataLoading &&
      !loading &&
      data &&
      !isLaunchFullyPropagated &&
      latestLaunchBuildResult?.__typename === 'BuildSuccess'
    ) {
      fetchSupergraphAndApiSchema();
    }

    if (isLaunchFullyPropagated) {
      setShouldPollForApiAndSupergraphSchema(false);
    }
  }, [
    isLaunchFullyPropagated,
    loading,
    data,
    supergraphAndApiDataLoading,
    fetchSupergraphAndApiSchema,
    latestLaunchBuildResult,
  ]);

  const schemaInfoMap = useMemo(() => {
    const lookup: Record<
      string,
      { old?: string; new: string } | { old: string; new?: string }
    > = {};
    if (loading) return lookup;

    const currentVisibleOrLatestSchemaPublish = selectedRevision
      ? selectedRevision.schemaPublish
      : variant?.activeSchemaPublish;

    const mergeBaseLaunch = variant?.proposal?.latestRevision?.mergeBaseLaunch;
    const mergeBaseSchemaPublish =
      variant?.proposal?.latestRevision?.mergeBaseSchemaPublish;

    // If no selected revision, diff against mergeBase otherwise diff against previous revision
    const previousPublishOrFirstSchemaPublish =
      selectedRevision?.previousRevision
        ? selectedRevision.previousRevision.schemaPublish
        : mergeBaseSchemaPublish;

    if (
      currentVisibleOrLatestSchemaPublish &&
      previousPublishOrFirstSchemaPublish
    ) {
      lookup[API_SCHEMA_ID] = {
        old: previousPublishOrFirstSchemaPublish.schema.hash,
        new: currentVisibleOrLatestSchemaPublish.schema.hash,
      };

      const previousRevisionOrFirstBuildResult =
        selectedRevision?.previousRevision
          ? selectedRevision.previousRevision.launch?.build?.result
          : mergeBaseLaunch?.build?.result;

      const currentVisibleOrLatestBuildResult = selectedRevision
        ? selectedRevision.launch?.build?.result
        : variant?.activeSchemaPublish?.launch?.build?.result;

      if (
        previousRevisionOrFirstBuildResult?.__typename === 'BuildSuccess' &&
        currentVisibleOrLatestBuildResult?.__typename === 'BuildSuccess'
      ) {
        lookup[SUPERGRAPH_SCHEMA_ID] = {
          old: previousRevisionOrFirstBuildResult.coreSchema.coreHash,
          new: currentVisibleOrLatestBuildResult.coreSchema.coreHash,
        };
      }
    }

    const currentRevisionOrLatestBuildInput = selectedRevision
      ? selectedRevision?.launch?.buildInput.__typename ===
        'CompositionBuildInput'
        ? selectedRevision.launch.buildInput
        : undefined
      : buildInput;

    if (isMinLengthArray(1, currentRevisionOrLatestBuildInput?.subgraphs)) {
      currentRevisionOrLatestBuildInput?.subgraphs.forEach((subgraphConfig) => {
        lookup[subgraphConfig.name] = {
          new: subgraphConfig.hash,
        };
      });
      const firstBuildInput = variant?.firstLaunch?.[0]?.buildInput;
      const previousRevisionBuildInput =
        selectedRevision?.previousRevision?.launch?.buildInput;
      if (
        !selectedRevision &&
        mergeBaseLaunch?.buildInput?.__typename === 'CompositionBuildInput'
      ) {
        mergeBaseLaunch?.buildInput?.subgraphs.forEach((subgraphConfig) => {
          getOrSetRecord(
            subgraphConfig.name,
            { old: subgraphConfig.hash },
            lookup,
          ).old = subgraphConfig.hash;
        });
      } else if (
        previousRevisionBuildInput?.__typename === 'CompositionBuildInput'
      ) {
        previousRevisionBuildInput.subgraphs.forEach((subgraphConfig) => {
          getOrSetRecord(
            subgraphConfig.name,
            { old: subgraphConfig.hash },
            lookup,
          ).old = subgraphConfig.hash;
        });
      } else if (firstBuildInput?.__typename === 'CompositionBuildInput') {
        firstBuildInput.subgraphs.forEach((subgraphConfig) => {
          getOrSetRecord(
            subgraphConfig.name,
            { old: subgraphConfig.hash },
            lookup,
          ).old = subgraphConfig.hash;
        });
      } else {
        throw new Error(`unknown type ${firstBuildInput?.__typename}`);
      }
    }

    return lookup;
  }, [
    loading,
    selectedRevision,
    variant?.activeSchemaPublish,
    variant?.proposal?.latestRevision?.mergeBaseLaunch,
    variant?.proposal?.latestRevision?.mergeBaseSchemaPublish,
    variant?.firstLaunch,
    buildInput,
  ]);

  const schemaNames = useMemo(
    () =>
      Object.entries(schemaInfoMap)
        .filter(([, hashes]) => hashes.old !== hashes.new)
        .map(([name]) => name)
        .sort((s1, s2) => s1.localeCompare(s2)),
    [schemaInfoMap],
  );

  const [composedSchemaNames, subgraphSchemaNames] = useMemo(() => {
    return _.partition(
      schemaNames,
      (name) => name === API_SCHEMA_ID || name === SUPERGRAPH_SCHEMA_ID,
    );
  }, [schemaNames]);

  const selectedSchemaInfo = selectedSchema
    ? schemaInfoMap[selectedSchema]
    : null;

  const {
    data: baseSdlData,
    error: baseSdlError,
    loading: baseSdlLoading,
  } = useQuery<
    GraphQLTypes.ProposalChangesBaseSdlQuery,
    GraphQLTypes.ProposalChangesBaseSdlQueryVariables
  >(
    gql`
      query ProposalChangesBaseSdlQuery(
        $graphId: ID!
        $newSdlHash: SHA256
        $oldSdlHash: SHA256
      ) {
        service(id: $graphId) {
          id
          flatDiff(newSdlHash: $newSdlHash, oldSdlHash: $oldSdlHash) {
            ... on FlatDiff {
              id
              ...DiffSummaryDiffFragment
              diff {
                ...FlatDiffItemFragment
              }
            }
          }
        }
      }
      ${DiffSummaryDiffFragment}
      ${FlatDiffItemFragment}
    `,
    {
      skip: !selectedSchemaInfo?.new && !selectedSchemaInfo?.old,
      variables: {
        graphId: graphRef.graphId,
        newSdlHash: selectedSchemaInfo?.new,
        oldSdlHash: selectedSchemaInfo?.old,
      },
    },
  );

  const diffResult = baseSdlData?.service?.flatDiff;
  const diff = diffResult?.__typename === 'FlatDiff' ? diffResult : null;

  const [l3MenuWidth, setL3MenuWidth] = useState(320);

  const [collapsedState, setCollapsedState] = useState<Record<string, boolean>>(
    {},
  );
  const someDetailsOpen = useMemo(
    () => Object.values(collapsedState).some((v) => !v),
    [collapsedState],
  );

  const handleToggleShowUnimplementedChanges = useCallback(() => {
    history.push(
      proposalChangesRouteConfig.locationFrom({
        location,
        fromRouteConfig: proposalChangesRouteConfig,
        patch: {
          includeImplementedChanges: includeImplementedChanges
            ? 'false'
            : 'true',
        },
      }),
    );
  }, [history, includeImplementedChanges, location]);

  // When we switch pages or navigate via back/forward on the browser
  // the scroll position should be updated to the top of the list
  const containerToResetScrollPositionUponNavigationRef =
    useRef<HTMLDivElement>(null);
  const containerToResetScrollPositionUponNavigation =
    containerToResetScrollPositionUponNavigationRef.current;
  useEffect(() => {
    containerToResetScrollPositionUponNavigation?.scrollTo(0, 0);
  }, [containerToResetScrollPositionUponNavigation, selectedSchema]);

  const parentComments = useMemo(
    () =>
      proposal?.parentComments
        .filter(
          (
            c,
          ): c is Extract<
            typeof c,
            { __typename: 'ParentChangeProposalComment' }
          > =>
            c.__typename === 'ParentChangeProposalComment' &&
            ((!graph?.roles?.canQueryImplementingServices &&
              selectedSchema === API_SCHEMA_ID) ||
              selectedSchema === c.schemaScope),
        )
        .sort(
          (c1, c2) =>
            Date.parse(c1.createdAt.toString()) -
            Date.parse(c2.createdAt.toString()),
        ) ?? [],
    [
      selectedSchema,
      graph?.roles?.canQueryImplementingServices,
      proposal?.parentComments,
    ],
  );

  const commentThreadToOpenId = useMemo(
    () =>
      parentComments.find(
        ({ schemaCoordinate, id }) =>
          schemaCoordinate === linkedCoordinateName &&
          (!linkedCommentId || linkedCommentId === id),
      )?.id,
    [parentComments, linkedCoordinateName, linkedCommentId],
  );

  const implementedChangesBySchema: Record<
    string,
    Omit<
      GraphQLTypes.ImplementedChangesFragment['implementedChanges'][0],
      'subgraph'
    >[]
  > = useMemo(
    () => ({
      ..._.groupBy(graph?.variant?.proposal?.implementedChanges, 'subgraph'),
      [API_SCHEMA_ID]: graph?.variant?.proposal?.implementedChanges ?? [],
      [SUPERGRAPH_SCHEMA_ID]:
        graph?.variant?.proposal?.implementedChanges ?? [],
    }),
    [graph],
  );

  const latestRevision = proposal?.latestRevision;
  const error = mainError ?? baseSdlError;
  if (loading || permissionsLoading) {
    return <Loading />;
  } else if (error) {
    return <ErrorPage error={error} />;
  } else if (!variant || !proposal) {
    return <NotFound />;
  } else if (
    latestRevision?.summary === 'initial launch' &&
    !linkedSelectedRevision
  ) {
    return (
      <Card className="m-8 flex h-full items-center justify-center">
        <EmptyState />
      </Card>
    );
  } else if (!selectedSchema && schemaNames.length > 0) {
    return (
      <Redirect
        to={proposalChangesRouteConfig.locationFrom({
          location,
          patch: { selectedSchema: schemaNames[0] },
        })}
      />
    );
  }

  const hasBuildErrors = latestLaunchBuildResult?.__typename === 'BuildFailure';
  const buildErrors =
    latestLaunchBuildResult?.__typename === 'BuildFailure'
      ? latestLaunchBuildResult.errorMessages
      : undefined;

  const selectedComposedSchema =
    selectedSchema === API_SCHEMA_ID || selectedSchema === SUPERGRAPH_SCHEMA_ID;

  const shouldShowCommentDrawer = !!linkedGroupName && !!linkedCoordinateName;

  const showTopSubgraphList =
    (containerSize?.width ?? 1024) -
      ((shouldShowCommentDrawer || shouldShowRevisionHistoryDrawer) &&
      (containerSize.width ?? 1000) > 1024
        ? 416
        : 0) <
    1024;
  const sourceGraphVariant = variant.proposal?.sourceVariant.name;

  // When the revision history drawer isn't open (i.e. no revision is selected,
  // the cached data is an empty object and we should use latest revision instead)
  const currentVisibleRevision = isMinLengthArray(
    1,
    Object.keys(cachedSelectedRevisionInfo ?? {}),
  )
    ? (cachedSelectedRevisionInfo ?? selectedRevision)
    : (selectedRevision ?? latestRevision);

  return (
    <>
      <PageLayout
        className={classnames(
          shouldShowCommentDrawer || shouldShowRevisionHistoryDrawer
            ? 'pl-8'
            : 'px-8',
        )}
        ref={containerSize.ref}
        l3MenuProps={{
          minWidth: 200,
          maxWidth: 600,
          setWidth: setL3MenuWidth,
          width: l3MenuWidth,
        }}
        l3Menu={
          !showTopSubgraphList && (
            <div className="mb-1 mt-6 flex flex-1 flex-col space-y-6 overflow-y-auto">
              {shouldShowRevisionHistoryUI && currentVisibleRevision && (
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="text-xl font-semibold">
                      Revision {currentVisibleRevision.id.substring(0, 8)}
                    </div>
                    <Tooltip label="View revision history">
                      <IconButton
                        size="sm"
                        variant="hidden"
                        className="ml-2"
                        aria-label="View Revision History"
                        icon={<IconHistory className="size-4 text-grey-dark" />}
                        as={ReactRouterLink}
                        to={proposalChangesRouteConfig.locationFrom({
                          location,
                          patch: {
                            linkedCoordinateName: RouteConfig.DELETE,
                            linkedGroupName: RouteConfig.DELETE,
                            includeImplementedChanges: RouteConfig.DELETE,
                            selectedRevision: currentVisibleRevision.id,
                          },
                        })}
                      />
                    </Tooltip>
                    <Tooltip label="View revision checks">
                      <IconButton
                        size="sm"
                        variant="hidden"
                        className="ml-2"
                        aria-label="Close Revision History"
                        icon={<IconCheck className="size-4 text-grey-dark" />}
                        as={ReactRouterLink}
                        to={proposalChecksListRouteConfig.locationFrom({
                          location,
                          patch: {
                            subgraph:
                              selectedSchema === API_SCHEMA_ID ||
                              selectedSchema === SUPERGRAPH_SCHEMA_ID
                                ? undefined
                                : selectedSchema,
                            shouldHighlightLatest: 'true',
                          },
                          fromRouteConfig: proposalChangesRouteConfig,
                        })}
                      />
                    </Tooltip>
                  </div>
                  {currentVisibleRevision?.summary && (
                    <p className="text-base text-secondary">
                      {safeCompileMarkdown(currentVisibleRevision.summary)}
                    </p>
                  )}
                  <div className="text-sm text-secondary">
                    {currentVisibleRevision?.createdBy?.__typename ===
                    'User' ? (
                      <>
                        <Avatar
                          src={
                            currentVisibleRevision.createdBy?.avatarUrl ??
                            undefined
                          }
                          name={currentVisibleRevision.createdBy.fullName}
                          size="xs"
                          className="mr-2"
                        />
                        <RelativeTimestamp value={proposal.updatedAt} /> by{' '}
                        <span className="font-semibold text-primary">
                          {currentVisibleRevision.createdBy.fullName}
                        </span>
                      </>
                    ) : (
                      <>
                        <Avatar size="xs" className="mr-2" />
                        <RelativeTimestamp
                          value={proposal.updatedAt}
                          className="mr-1"
                        />{' '}
                        by{' '}
                        <span className="font-semibold text-primary">
                          Unknown
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
              {isMinLengthArray(1, composedSchemaNames) && selectedSchema && (
                <>
                  <h4 className="border-b border-primary pb-2 text-base font-semibold text-primary">
                    Composed schemas
                  </h4>
                  <div className="flex flex-col gap-1">
                    {composedSchemaNames.map((schema) => {
                      return (
                        <SchemaLink
                          key={schema}
                          selectedSchemaName={selectedSchema}
                          schemaName={schema}
                          buildErrors={buildErrors}
                        />
                      );
                    })}
                  </div>
                </>
              )}
              {isMinLengthArray(1, subgraphSchemaNames) && selectedSchema && (
                <>
                  <h4 className="border-b border-primary pb-2 text-base font-semibold text-primary">
                    Subgraph schemas
                  </h4>
                  <div className="flex flex-col gap-1">
                    {subgraphSchemaNames?.map((schema) => {
                      return (
                        <SchemaLink
                          key={schema}
                          selectedSchemaName={selectedSchema}
                          schemaName={schema}
                          buildErrors={buildErrors}
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )
        }
      >
        <div className="relative flex size-full">
          <div className="flex w-0 flex-1 flex-col">
            {showTopSubgraphList && selectedSchema && (
              <div className="flex flex-wrap gap-2 px-2 pb-3 pt-6">
                {schemaNames.map((schema) => (
                  <SchemaLink
                    key={schema}
                    selectedSchemaName={selectedSchema}
                    schemaName={schema}
                    className="flex-none [&:not([aria-current=page])]:border-primary"
                    buildErrors={buildErrors}
                  />
                ))}
              </div>
            )}
            <div
              className={classnames(
                'h-full overflow-y-auto px-2 pb-8',
                showTopSubgraphList ? 'pt-1' : 'pt-6',
              )}
              ref={containerToResetScrollPositionUponNavigationRef}
            >
              <Card className="grow self-start pb-4">
                <div
                  className={classnames(
                    'flex items-center border-b border-primary p-4',
                    !latestRevision ? 'justify-end' : 'justify-between',
                    shouldShowRevisionHistoryUI &&
                      !showTopSubgraphList &&
                      'hidden',
                  )}
                >
                  {currentVisibleRevision && currentVisibleRevision && (
                    <div className="flex items-center">
                      {!shouldShowRevisionHistoryUI ? (
                        <>
                          <div className="mr-3 flex size-7 flex-none items-center justify-center rounded-full border border-black-lighter border-opacity-[0.2] bg-blilet">
                            <IconCommit className="text-inverted" />
                          </div>
                          <div>
                            Last revision made by{' '}
                            {currentVisibleRevision.createdBy?.__typename ===
                            'User'
                              ? currentVisibleRevision.createdBy.fullName
                              : 'Unknown'}{' '}
                            on <LocalTimestamp value={proposal.updatedAt} />
                          </div>
                          <Link
                            to={proposalChecksListRouteConfig.locationFrom({
                              fromRouteConfig: proposalChangesRouteConfig,
                              location,
                              patch: {
                                subgraph:
                                  selectedSchema === API_SCHEMA_ID ||
                                  selectedSchema === SUPERGRAPH_SCHEMA_ID
                                    ? undefined
                                    : selectedSchema,
                                shouldHighlightLatest: 'true',
                              },
                            })}
                          >
                            View latest checks
                          </Link>
                        </>
                      ) : (
                        showTopSubgraphList && (
                          <>
                            <div className="mr-3 flex size-7 flex-none items-center justify-center rounded-full border border-black-lighter border-opacity-[0.2] bg-blilet">
                              <IconCommit className="text-inverted" />
                            </div>
                            <div>
                              Revision{' '}
                              <span className="font-semibold">
                                {currentVisibleRevision.id.substring(0, 8)}
                              </span>{' '}
                              made{' '}
                              <RelativeTimestamp value={proposal.updatedAt} />{' '}
                              by{' '}
                              <span className="font-semibold">
                                {currentVisibleRevision.createdBy
                                  ?.__typename === 'User'
                                  ? currentVisibleRevision.createdBy.fullName
                                  : 'Unknown'}
                              </span>
                            </div>
                            <Tooltip label="View revision history">
                              <IconButton
                                size="sm"
                                variant="hidden"
                                className="ml-2"
                                aria-label="View Revision History"
                                icon={<IconHistory />}
                                as={ReactRouterLink}
                                to={proposalChangesRouteConfig.locationFrom({
                                  location,
                                  patch: {
                                    linkedCoordinateName: RouteConfig.DELETE,
                                    linkedGroupName: RouteConfig.DELETE,
                                    selectedRevision: currentVisibleRevision.id,
                                  },
                                })}
                              />
                            </Tooltip>
                            <Tooltip label="View revision changes">
                              <IconButton
                                size="sm"
                                variant="hidden"
                                className="ml-2"
                                aria-label="View revision changes"
                                icon={<IconCheck />}
                                as={ReactRouterLink}
                                to={proposalChecksListRouteConfig.locationFrom({
                                  location,
                                  patch: {
                                    subgraph:
                                      selectedSchema === API_SCHEMA_ID ||
                                      selectedSchema === SUPERGRAPH_SCHEMA_ID
                                        ? undefined
                                        : selectedSchema,
                                    shouldHighlightLatest: 'true',
                                  },
                                  fromRouteConfig: proposalChangesRouteConfig,
                                })}
                              />
                            </Tooltip>
                          </>
                        )
                      )}
                    </div>
                  )}

                  {graph?.variant?.proposal?.implementedChanges &&
                    !shouldShowRevisionHistoryUI && (
                      <div className="mr-4">
                        <FormControl className="flex flex-row items-center">
                          <FormLabel>Show unimplemented changes only</FormLabel>
                          <Tooltip
                            label={
                              !includeImplementedChanges
                                ? 'Show all changes'
                                : 'Show unimplemented changes only'
                            }
                          >
                            <Switch
                              isChecked={!includeImplementedChanges}
                              onChange={handleToggleShowUnimplementedChanges}
                            />
                          </Tooltip>
                        </FormControl>
                      </div>
                    )}
                </div>
                {hasBuildErrors && selectedComposedSchema && (
                  <Alert status="warning" className="-mt-[1px]">
                    <span>
                      This variant of the{' '}
                      {selectedSchema === API_SCHEMA_ID ? 'API' : 'Supergraph'}{' '}
                      schema does not include the latest from some of your
                      subgraphs because the supergraph schema build failed.{' '}
                      <Link
                        className="inline-block"
                        to={setOverlayVisibleLocation({
                          location,
                          overlayName: Config.modals.buildErrors,
                        })}
                      >
                        See details
                      </Link>
                    </span>
                  </Alert>
                )}
                <PermissionGuard
                  graphId={graphRef.graphId}
                  graphVariant={graphRef.graphVariant}
                  graphVariantPermissions="canQueryLaunches"
                  fallback={null}
                >
                  {!hasBuildErrors &&
                    selectedComposedSchema &&
                    !isLaunchFullyPropagated && (
                      <Alert status="warning" className="-mt-[1px]">
                        Currently composing your graph! Your recent changes will
                        be reflected here shortly.
                      </Alert>
                    )}
                </PermissionGuard>

                {selectedSchemaInfo ? (
                  baseSdlLoading || selectedRevisionLoading ? (
                    <div className="p-8">
                      <Loading />
                    </div>
                  ) : (
                    <div className="px-8 py-4">
                      {graph?.variant?.proposal?.implementedChanges &&
                        !shouldShowRevisionHistoryDrawer && (
                          <div className="mb-4">
                            <FormControl className="flex flex-row items-center">
                              <FormLabel className="font-normal">
                                Show unimplemented changes only
                              </FormLabel>
                              <Tooltip
                                label={
                                  !includeImplementedChanges
                                    ? 'Show all changes'
                                    : 'Show unimplemented changes only'
                                }
                              >
                                <Switch
                                  isChecked={!includeImplementedChanges}
                                  onChange={
                                    handleToggleShowUnimplementedChanges
                                  }
                                />
                              </Tooltip>
                            </FormControl>
                          </div>
                        )}
                      <SummaryHeader />
                      {diff && (
                        <DiffSummary
                          subgraphName={selectedSchema}
                          shouldLinkToChangesPage
                          diff={diff}
                        />
                      )}

                      {diff && diff.diff.length > 0 && selectedSchema && (
                        <>
                          <h1 className="pt-10 text-2xl font-semibold">
                            Detailed view{' '}
                            <Tooltip
                              label={
                                someDetailsOpen
                                  ? 'Collapse all detailed views'
                                  : 'Expand all detailed views'
                              }
                            >
                              <IconButton
                                className="ml-2 text-secondary"
                                size="sm"
                                variant="secondary"
                                aria-label={
                                  someDetailsOpen
                                    ? 'collapse all detailed views'
                                    : 'expand all detailed views'
                                }
                                icon={
                                  someDetailsOpen ? (
                                    <IconCollapseList />
                                  ) : (
                                    <IconExpandList />
                                  )
                                }
                                onClick={() => {
                                  setCollapsedState(
                                    _.mapValues(
                                      collapsedState,
                                      () => someDetailsOpen,
                                    ),
                                  );
                                }}
                              />
                            </Tooltip>
                          </h1>
                          <div className="pt-1 text-secondary">
                            An in-depth view into proposed schema changes for
                            this subgraph.
                          </div>
                          <ChangesDiffs
                            proposalStatus={variant.proposal?.status}
                            canQueryLaunches={canQueryLaunches}
                            sourceGraphVariant={sourceGraphVariant}
                            implementedChanges={
                              implementedChangesBySchema[selectedSchema] ?? []
                            }
                            includeImplementedChanges={
                              includeImplementedChanges
                            }
                            collapsedState={collapsedState}
                            setCollapsedState={setCollapsedState}
                            oldSchemaHash={selectedSchemaInfo.old}
                            newSchemaHash={selectedSchemaInfo.new}
                            graphRef={graphRef}
                            parentComments={parentComments}
                            canMakeComments={!shouldShowRevisionHistoryDrawer}
                          />
                        </>
                      )}
                    </div>
                  )
                ) : shouldShowRevisionHistoryDrawer ? (
                  // In the case that composition fails for a particular revision, there is no api/supergraph schema in the
                  // schemaInfoMap. We should show a message to the user that the composed schema is not available
                  <SummarySection>
                    <i className="text-secondary">
                      The composed schema for this revision is not available due
                      to composition failure.
                    </i>
                  </SummarySection>
                ) : (
                  <SummarySection>
                    <i className="text-secondary">
                      There are no semantic changes associated with this
                      proposal and the source variant.
                    </i>
                  </SummarySection>
                )}
              </Card>
            </div>
          </div>
          <ProposalCommentDrawer
            isOpen={shouldShowCommentDrawer}
            onClose={onCloseDrawer}
            className="max-lg:absolute max-lg:right-0 max-lg:top-0"
            graphRef={graphRef}
            sourceGraphVariant={sourceGraphVariant}
            canQueryLaunches={canQueryLaunches}
            schemaScope={selectedSchema}
            proposalId={proposal.id}
            commentThreadId={commentThreadToOpenId}
            parentComments={parentComments.filter(
              (c) =>
                !c.outdated ||
                // TODO(evan): coordinates with targets are incorrectly marked as outdated after a subsequent publish
                // we should remove this check once we fix the issue
                c.schemaCoordinate.endsWith('~returnType') ||
                c.schemaCoordinate.endsWith('~description'),
            )}
            implementedChanges={
              selectedSchema
                ? (implementedChangesBySchema[selectedSchema] ?? [])
                : []
            }
            isStale={isStale}
          />
          {shouldShowRevisionHistoryUI && (
            <RevisionHistoryDrawer
              isOpen={!!shouldShowRevisionHistoryDrawer}
              onClose={onCloseDrawer}
              graphRef={graphRef}
              className="max-lg:absolute max-lg:right-0 max-lg:top-0"
            />
          )}
        </div>
      </PageLayout>
      <LazyUrlModal name={Config.modals.deleteProposalComment}>
        {({ closeModal, isOpen }) => (
          <DeleteCommentModal
            proposalId={proposal.id}
            graphRef={graphRef}
            isOpen={isOpen}
            onClose={() =>
              closeModal(undefined, ['proposalCommentId', 'commentThreadId'])
            }
          />
        )}
      </LazyUrlModal>
    </>
  );
};
